import type { QuestionRatingModelData } from '@coorum-research/shared-common/types/survey-json.type'
import type { Question } from 'survey-core'
import { cn } from '../../utils'
import { useTheme } from '../ThemeContext'
import { RatingStarIcon } from '../icons/RatingStarIcon'

type Props = {
  question: Question
}

/**
 * answer 回答表示用のコンポーネント
 * question type === 'rating'
 */
export function SurveyQuestionAnswerRating({ question }: Props) {
  const themeJson = useTheme()
  const modelData = question.toJSON() as QuestionRatingModelData
  const value = question.value as number || 0

  // rating設定値
  const rateCount = modelData.rateCount ?? 5
  const rateMin = modelData.rateMin ?? 1
  const rateMax = modelData.rateMax ?? rateCount
  const rateType = modelData.rateType ?? 'stars'

  // 星自体の表示
  const renderStars = () => {
    const stars = []
    for (let i = rateMin; i <= rateMax; i++) {
      const isFilled = i <= value

      stars.push(
        <div
          key={i}
          className="inline-flex items-center justify-center px-0.5"
          title={`${i}星`}
        >
          <div className="relative" style={{ width: '48px', height: '48px' }}>
            <RatingStarIcon filled={isFilled} color={themeJson.mainColor} width={48} height={48} className="absolute inset-0" />
          </div>
        </div>,
      )
    }
    return stars
  }

  // ラベル評価の表示
  const renderLabels = () => {
    if (!modelData.rateValues) {
      // デフォルトのラベル
      const labels = []
      for (let i = rateMin; i <= rateMax; i++) {
        const isSelected = i === value

        labels.push(
          <div
            key={i}
            className={cn(
              'inline-flex items-center justify-center px-3 py-1 border rounded text-sm',
              isSelected
                ? 'bg-blue-500 text-white border-blue-500'
                : 'bg-gray-100 text-gray-600 border-gray-300',
            )}
          >
            {i}
          </div>,
        )
      }
      return labels
    }

    // カスタムラベル
    return modelData.rateValues.map((rateValue) => {
      const rateNum = typeof rateValue === 'number' ? rateValue : rateValue.value
      const rateText = typeof rateValue === 'number' ? String(rateValue) : rateValue.text
      const isSelected = rateNum === value

      return (
        <div
          key={rateNum}
          className={cn(
            'inline-flex items-center justify-center px-3 py-1 border rounded text-sm',
            isSelected
              ? 'bg-blue-500 text-white border-blue-500'
              : 'bg-gray-100 text-gray-600 border-gray-300',
          )}
        >
          {rateText}
        </div>
      )
    })
  }

  return (
    <>
      <div className="flex justify-start gap-2">
        {rateType === 'stars' ? renderStars() : renderLabels()}
      </div>

      {modelData.showCommentArea && question.comment.length > 0 && <div className="mt-2">{question.comment}</div>}
    </>
  )
}
